import {
  arbeitsRang,
  DAILY_ERLEDIGT_TAGE,
  DAILY_VORLAUF_ZEILEN,
  type Termin,
} from '@netcon-work/shared'
import type { Aufgabe } from '../../lib/queries'

/**
 * **Die Runde des Dailys – drei Spuren je Entwickler und ein Streifen**
 * ([Z-18](../../../../../docs/phasen/zusatz-18-daily.md), AP 18.3; der Streifen seit
 * [E-371](../../../../../docs/ENTSCHEIDUNGEN.md#e-371)).
 *
 * Nils: *„… mit den Aufgaben die Erledigt wurden (letzten 7 Tage), die Aufgaben die «in Arbeit»
 * stehen, die nächsten Aufgaben aus dem Vorlauf (7 Stück)."*
 *
 * ## Warum das hier gerechnet wird und nicht auf dem Server
 *
 * Der Aufgabenbestand liegt beim Start vollständig im Client ([Leitplanke
 * 2](../../../../../CLAUDE.md)) und wird per WebSocket aktuell gehalten. Die Runde ist damit
 * eine **lokale** Auswertung – kein Ladezustand, kein Spinner, und beim Umschalten der Person
 * passiert nichts als ein Neuzeichnen.
 *
 * ## Die einfache Regel – und warum sie einfach ist
 *
 * „Als Nächstes" ist hier **nicht** derselbe Vorlauf wie in „Mein Tag". Der entsteht in
 * `useMeinTagLage()` und ist auf die **angemeldete** Person zugeschnitten: Er mischt
 * Wiedervorlagen, Eigenes und den ziehbaren Vorrat, und was ziehbar ist, hängt an den Pools und
 * Arbeitswelten *dieser* Person. Für eine andere Person ließe sich das nur nachbauen – zwei
 * Stellen mit derselben Absicht, die irgendwann auseinanderlaufen.
 *
 * Nils hat die einfache Regel gewählt (21.09.2026): **was der Person gehört oder auf sie
 * gewünscht ist, nach Frist und Priorität.** Im Daily geht es darum, *worüber gesprochen wird* –
 * nicht darum, die Reihenfolge einer fremden Ansicht zeilengenau zu spiegeln. Eine Liste, die
 * vorgäbe „Mein Tag" zu sein und es in Randfällen nicht ist, wäre schlechter als eine, die es
 * gar nicht erst behauptet.
 */

/** Eine Zeile in einer der Spuren. Nur, was im Daily angezeigt wird. */
export interface RundenZeile {
  id: string
  titel: string
  /** Für den farbigen Punkt: woher die Aufgabe stammt. */
  quelle: string | null
  /**
   * Tage seit dem Wechsel in den jetzigen Status – die Zahl hinter „in Arbeit". `null`, wenn
   * kein Statuswechsel aufgezeichnet ist.
   */
  tage: number | null
  /**
   * **Auf wen gewartet wird** (23.09.2026, [E-371](../../../../../docs/ENTSCHEIDUNGEN.md#e-371)) –
   * nur an Zeilen der Abnahme-Spur belegt. Im Daily ist das die eigentliche Auskunft: Die
   * Aufgabe liegt nicht bei der Person, über die gerade gesprochen wird.
   */
  abnehmer: string | null
}

export interface Runde {
  personId: string
  erledigt: RundenZeile[]
  inArbeit: RundenZeile[]
  /**
   * **Wartet auf Abnahme** (E-371). Eigene Spur, seit Nils gemeldet hat, dass diese Aufgaben
   * unter „Als Nächstes" mitliefen: Dort standen sie nicht aus Absicht, sondern weil die
   * Bedingung dort alles nimmt, was offen ist – und `review` ist weder erledigt noch
   * zurückgestellt noch in Arbeit.
   */
  abnahme: RundenZeile[]
  naechstes: RundenZeile[]
}

/** Wem gehört die Aufgabe in **Work** – und ersatzweise, wen die Quelle führt. */
function gehoert(a: Aufgabe, personId: string): boolean {
  return a.workAssigneeId === personId || (a.workAssigneeId === null && a.assigneeId === personId)
}

function gewuenschtFuer(a: Aufgabe, personId: string): boolean {
  return a.wunschIds?.includes(personId) ?? false
}

/**
 * **Seit wann die Aufgabe in ihrem Status steht** – je Aufgaben-ID, vom Server (`daily.seit`).
 *
 * Nicht `updatedAt`: Das setzt der Import bei jedem Lauf neu. Daran gemessen galt jede alte
 * erledigte Aufgabe, die eingelesen wurde, als „diese Woche fertig" (Nils, 20.10.2026: 49
 * Zeilen bei Davin).
 */
export type StatusSeit = Readonly<Record<string, string>>

function seitMs(a: Aufgabe, seit: StatusSeit): number | null {
  const wert = seit[a.id]
  const ms = wert ? new Date(wert).getTime() : Number.NaN
  return Number.isFinite(ms) ? ms : null
}

/** Ohne aufgezeichneten Wechsel hinten – sie haben keine Dauer, über die man reden könnte. */
function nachSeit(seit: StatusSeit) {
  return (a: Aufgabe, b: Aufgabe) =>
    (seitMs(a, seit) ?? Number.POSITIVE_INFINITY) - (seitMs(b, seit) ?? Number.POSITIVE_INFINITY)
}

function zeile(a: Aufgabe, jetzt: number, seit: StatusSeit): RundenZeile {
  const gewechselt = seitMs(a, seit)
  const tage =
    gewechselt !== null ? Math.max(0, Math.floor((jetzt - gewechselt) / 86_400_000)) : null
  return {
    id: a.id,
    titel: a.title,
    quelle: a.sourceId ?? null,
    tage,
    abnehmer: a.abnahmeDurchName ?? null,
  }
}

/**
 * **Die Spuren für eine Person.**
 *
 * `jetzt` wird hereingegeben und nicht hier geholt: Eine Funktion, die die Uhr selbst liest,
 * lässt sich nicht prüfen – und eine Runde, die im Test von der Tageszeit abhängt, ist genau
 * die Sorte Test, die irgendwann nachts rot wird.
 */
export function rundeFuer(
  aufgaben: readonly Aufgabe[],
  personId: string,
  jetzt: number,
  /**
   * **Der wirksame Termin je Aufgabe** (Z-20, 06.10.2026). Hier stand eine eigene Kopie der
   * Kalender-Regel (Fälligkeit → Priorität → Änderung); jetzt ist es dieselbe Rangfolge wie im
   * Vorlauf (`arbeitsRang`), aus Sicht der Person – ihr ★ zählt wie in ihrem „Mein Tag".
   * Fehlt die Tafel, gilt die Fälligkeit.
   */
  termine?: ReadonlyMap<string, Termin>,
  /** Fehlt sie (noch nicht geladen), ist „Erledigt" leer – lieber leer als falsch. */
  seit: StatusSeit = {},
): Runde {
  const grenze = jetzt - DAILY_ERLEDIGT_TAGE * 86_400_000

  const erledigt = aufgaben
    .filter((a) => {
      if (a.status !== 'erledigt' || !gehoert(a, personId)) return false
      const fertig = seitMs(a, seit)
      return fertig !== null && fertig >= grenze
    })
    /** Das zuletzt Fertige zuerst – darüber spricht man, nicht über das von Montag. */
    .sort((a, b) => nachSeit(seit)(b, a))
    .map((a) => zeile(a, jetzt, seit))

  const inArbeit = aufgaben
    .filter((a) => a.status === 'in_arbeit' && gehoert(a, personId))
    .sort(nachSeit(seit))
    .map((a) => zeile(a, jetzt, seit))

  /**
   * **Was auf eine Abnahme wartet** (23.09.2026,
   * [E-371](../../../../../docs/ENTSCHEIDUNGEN.md#e-371)).
   *
   * Nils: *„bei «Als nächstes» im Daily stehen auch die Review/Abnahme Aufgaben noch drin."*
   *
   * **Nur, was der Person gehört** – anders als bei „Als Nächstes" zählt der Wunsch hier nicht:
   * Eine Aufgabe, die jemand anderes zur Abnahme gegeben hat, wartet nicht auf *diese* Person,
   * und in ihrer Runde wäre sie eine fremde Zeile.
   *
   * **Das Älteste zuerst.** Was am längsten wartet, ist der Fall, über den zu sprechen ist;
   * die Frage in der Runde lautet „wer nimmt das endlich ab", nicht „was ist neu".
   */
  const abnahme = aufgaben
    .filter((a) => a.status === 'review' && gehoert(a, personId))
    .sort(nachSeit(seit))
    .map((a) => zeile(a, jetzt, seit))

  /**
   * **Was offen ist und dieser Person zugedacht** – ohne das, was schon läuft: Es steht in der
   * Spur daneben, und zweimal dieselbe Zeile ist eine Zeile zu viel in einer Besprechung, die
   * zwanzig Minuten dauert.
   *
   * **Und ohne das, was auf Abnahme wartet** (E-371): Es liegt gerade nicht bei dieser Person,
   * sondern bei ihrem Abnehmer – unter „Als Nächstes" verdrängte es eine Zeile, über die
   * tatsächlich zu sprechen wäre.
   */
  const rang = arbeitsRang(personId, new Date(jetzt))
  const naechstes = aufgaben
    .filter(
      (a) =>
        a.status !== 'erledigt' &&
        a.status !== 'zurueckgestellt' &&
        a.status !== 'in_arbeit' &&
        a.status !== 'review' &&
        (gehoert(a, personId) || gewuenschtFuer(a, personId)),
    )
    .map((a) => ({
      a,
      rang: {
        ...(termine?.has(a.id) ? { termin: termine.get(a.id) ?? null } : {}),
        dueAt: a.dueAt ?? null,
        priority: a.priority ?? null,
        sourceCreatedAt: a.sourceCreatedAt ?? null,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
        /** In `tasks.all` eine Zeichenkette aus `GROUP_CONCAT` – erst hier eine Liste. */
        wunschIds: Array.isArray(a.wunschIds)
          ? a.wunschIds
          : String(a.wunschIds ?? '')
              .split(',')
              .filter(Boolean),
      },
    }))
    .sort((x, y) => rang(x.rang, y.rang))
    .map(({ a }) => a)
    .slice(0, DAILY_VORLAUF_ZEILEN)
    .map((a) => zeile(a, jetzt, seit))

  return { personId, erledigt, inArbeit, abnahme, naechstes }
}
